import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe, Copy, QrCode, Download, Share2, ExternalLink, Bell, BellOff, Mail, Volume2, VolumeX } from 'lucide-react';
import {
  pushSupported,
  enablePushNotifications,
  disablePushNotifications,
  getPushSubscriptionState,
  sendTestPush,
} from '@/lib/pushNotifications';
import {
  isSoundEnabled,
  enableSoundNotifications,
  disableSoundNotifications,
  playChime,
} from '@/lib/notificationSound';

const API_URL = process.env.REACT_APP_API_URL;

const OnlineBookingCard = () => {
  const { t } = useTranslation();
  const SLOT_INTERVALS = [
    { value: 15, label: t('onlineBooking.interval15') },
    { value: 30, label: t('onlineBooking.interval30') },
    { value: 60, label: t('onlineBooking.interval60') },
  ];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [slugDraft, setSlugDraft] = useState('');
  const [emailsDraft, setEmailsDraft] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [pushState, setPushState] = useState({ supported: false, permission: 'unsupported', subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const qrUrlRef = useRef(null);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/online-booking/settings`, { withCredentials: true });
      setData(data);
      setSlugDraft(data.slug || '');
      setEmailsDraft(data.notification_emails || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('onlineBooking.settingsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const refreshPushState = async () => {
    if (!pushSupported()) {
      setPushState({ supported: false, permission: 'unsupported', subscribed: false });
      return;
    }
    setPushState(await getPushSubscriptionState());
  };

  useEffect(() => {
    fetchSettings();
    refreshPushState();
    setSoundOn(isSoundEnabled());
    return () => {
      if (qrUrlRef.current) URL.revokeObjectURL(qrUrlRef.current);
    };
  }, []);

  const updateField = async (patch) => {
    setSaving(true);
    try {
      const { data: fresh } = await axios.put(`${API_URL}/api/online-booking/settings`, patch, {
        withCredentials: true,
      });
      setData(fresh);
      setSlugDraft(fresh.slug || '');
      toast.success(t('common.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('onlineBooking.settingsSaveFailed'));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleSlugSave = async () => {
    const trimmed = (slugDraft || '').trim();
    if (!trimmed || trimmed === data?.slug) return;
    try {
      await updateField({ slug: trimmed });
    } catch { /* toast already shown */ }
  };

  const handleCopy = async () => {
    if (!data?.public_url) return;
    try {
      await navigator.clipboard.writeText(data.public_url);
      toast.success(t('onlineBooking.linkCopied'));
    } catch {
      toast.error(t('onlineBooking.copyFailed'));
    }
  };

  const handleShowQr = async () => {
    setQrLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/online-booking/qr`, {
        withCredentials: true,
        responseType: 'blob',
      });
      if (qrUrlRef.current) URL.revokeObjectURL(qrUrlRef.current);
      const url = URL.createObjectURL(res.data);
      qrUrlRef.current = url;
      setData((prev) => ({ ...prev, _qrUrl: url }));
    } catch {
      toast.error(t('onlineBooking.qrFailed'));
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadQr = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/online-booking/qr?download=1`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paydaai-randevu-${data?.slug || 'qr'}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('onlineBooking.downloadFailed'));
    }
  };

  const handleWhatsAppShare = () => {
    if (!data?.public_url) return;
    const msg = `${data.public_url}`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleEmailsSave = async () => {
    const trimmed = (emailsDraft || '').trim();
    if (trimmed === (data?.notification_emails || '')) return;
    try {
      await updateField({ notification_emails: trimmed });
    } catch { /* toast shown */ }
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    await enablePushNotifications();
    await refreshPushState();
    setPushBusy(false);
  };
  const handleDisablePush = async () => {
    setPushBusy(true);
    await disablePushNotifications();
    await refreshPushState();
    setPushBusy(false);
  };
  const handleTestPush = async () => {
    setPushBusy(true);
    await sendTestPush();
    setPushBusy(false);
  };

  const handleEnableSound = () => {
    const ok = enableSoundNotifications();
    if (!ok) {
      toast.error(t('onlineBooking.soundUnsupported'));
      return;
    }
    setSoundOn(true);
    toast.success(t('onlineBooking.soundEnabled'));
  };
  const handleDisableSound = () => {
    disableSoundNotifications();
    setSoundOn(false);
    toast.success(t('onlineBooking.soundDisabled'));
  };
  const handleTestSound = () => {
    playChime();
  };

  if (loading) {
    return (
      <Card className="border border-[#E5E5E5]" data-testid="online-booking-card">
        <CardContent className="py-8 text-center text-sm text-[#595959]">{t('common.loading')}</CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#E5E5E5]" data-testid="online-booking-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-[#0F172A]">
          <Globe className="w-5 h-5 text-[#2563EB]" />
          {t('onlineBooking.cardTitle')}
        </CardTitle>
        <p className="text-sm text-[#595959]">{t('onlineBooking.cardSubtitle')}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enabled switch */}
        <div className="flex items-center justify-between p-4 rounded-lg border border-[#E5E5E5] bg-[#F9FAFB]" data-testid="ob-enabled-row">
          <div>
            <p className="text-sm font-medium text-[#0F172A]">{t('onlineBooking.enabledLabel')}</p>
            <p className="text-xs text-[#595959]">{data?.enabled ? t('onlineBooking.enabledOn') : t('onlineBooking.enabledOff')}</p>
          </div>
          <Switch
            checked={!!data?.enabled}
            disabled={saving}
            onCheckedChange={(v) => updateField({ enabled: v }).catch(() => {})}
            data-testid="ob-enabled-switch"
          />
        </div>

        {/* Slug + link */}
        <div className="space-y-2">
          <Label className="text-sm">{t('onlineBooking.slugLabel')}</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-stretch flex-1 rounded-lg border border-[#E5E5E5] overflow-hidden bg-white">
              <span className="px-3 py-2 text-sm text-[#595959] bg-[#F9FAFB] border-r border-[#E5E5E5] hidden sm:flex items-center">/r/</span>
              <Input
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value.toLowerCase())}
                onBlur={handleSlugSave}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                className="border-0 focus-visible:ring-0"
                placeholder={t('onlineBooking.slugPlaceholder')}
                data-testid="ob-slug-input"
              />
            </div>
          </div>
          {data?.public_url && (
            <div className="flex flex-wrap gap-2 pt-1">
              <div className="text-xs text-[#0F172A] bg-[#EFF6FF] border border-[#BFDBFE] rounded-md px-3 py-2 flex-1 break-all" data-testid="ob-public-url">
                {data.public_url}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={handleCopy} data-testid="ob-copy-btn">
                <Copy className="w-4 h-4 mr-1" /> {t('common.copy')}
              </Button>
              <a
                href={data.public_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-3 py-2 text-sm rounded-md border border-[#E5E5E5] hover:bg-[#F9FAFB]"
                data-testid="ob-open-btn"
              >
                <ExternalLink className="w-4 h-4 mr-1" /> {t('common.open')}
              </a>
              <Button type="button" variant="outline" size="sm" onClick={handleWhatsAppShare} data-testid="ob-whatsapp-btn">
                <Share2 className="w-4 h-4 mr-1" /> WhatsApp
              </Button>
            </div>
          )}
        </div>

        {/* QR */}
        <div className="space-y-2 border-t border-[#E5E5E5] pt-4">
          <Label className="text-sm">{t('onlineBooking.qrLabel')}</Label>
          <div className="flex flex-wrap items-start gap-3">
            {data?._qrUrl ? (
              <img
                src={data._qrUrl}
                alt="QR"
                className="w-40 h-40 border border-[#E5E5E5] rounded-md bg-white"
                data-testid="ob-qr-img"
              />
            ) : (
              <div className="w-40 h-40 border border-dashed border-[#E5E5E5] rounded-md flex items-center justify-center bg-[#F9FAFB] text-[#9CA3AF] text-xs text-center px-2" data-testid="ob-qr-placeholder">
                {t('onlineBooking.qrPlaceholder')}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleShowQr} disabled={qrLoading} data-testid="ob-show-qr-btn">
                <QrCode className="w-4 h-4 mr-1" /> {qrLoading ? t('onlineBooking.generating') : t('onlineBooking.showQr')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleDownloadQr} data-testid="ob-download-qr-btn">
                <Download className="w-4 h-4 mr-1" /> {t('onlineBooking.downloadQr')}
              </Button>
            </div>
          </div>
          <p className="text-xs text-[#595959]">{t('onlineBooking.qrHelp')}</p>
        </div>

        {/* Slot interval */}
        <div className="space-y-2 border-t border-[#E5E5E5] pt-4">
          <Label className="text-sm">{t('onlineBooking.intervalLabel')}</Label>
          <Select
            value={String(data?.slot_interval || 30)}
            onValueChange={(v) => updateField({ slot_interval: Number(v) }).catch(() => {})}
          >
            <SelectTrigger data-testid="ob-interval-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SLOT_INTERVALS.map((it) => (
                <SelectItem key={it.value} value={String(it.value)}>{it.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-[#595959]">{t('onlineBooking.intervalHelp')}</p>
        </div>

        {/* Staff required */}
        <div className="flex items-center justify-between p-4 rounded-lg border border-[#E5E5E5]" data-testid="ob-staff-required-row">
          <div>
            <p className="text-sm font-medium text-[#0F172A]">{t('onlineBooking.staffRequired')}</p>
            <p className="text-xs text-[#595959]">{t('onlineBooking.staffRequiredDesc')}</p>
          </div>
          <Switch
            checked={!!data?.staff_required}
            disabled={saving}
            onCheckedChange={(v) => updateField({ staff_required: v }).catch(() => {})}
            data-testid="ob-staff-required-switch"
          />
        </div>

        {/* Auto confirm */}
        <div className="flex items-center justify-between p-4 rounded-lg border border-[#E5E5E5]" data-testid="ob-auto-confirm-row">
          <div>
            <p className="text-sm font-medium text-[#0F172A]">{t('onlineBooking.autoConfirm')}</p>
            <p className="text-xs text-[#595959]">{data?.auto_confirm ? t('onlineBooking.autoConfirmOn') : t('onlineBooking.autoConfirmOff')}</p>
          </div>
          <Switch
            checked={!!data?.auto_confirm}
            disabled={saving}
            onCheckedChange={(v) => updateField({ auto_confirm: v }).catch(() => {})}
            data-testid="ob-auto-confirm-switch"
          />
        </div>

        {/* === Bildirimler === */}
        <div className="space-y-3 border-t border-[#E5E5E5] pt-4" data-testid="ob-notifications-section">
          <Label className="text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-[#2563EB]" /> {t('onlineBooking.notificationsTitle')}</Label>

          {/* E-posta */}
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1 text-[#595959]">
              <Mail className="w-3.5 h-3.5" /> {t('onlineBooking.notificationEmailsLabel')}
            </Label>
            <Input
              value={emailsDraft}
              onChange={(e) => setEmailsDraft(e.target.value)}
              onBlur={handleEmailsSave}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              placeholder={t('onlineBooking.notificationEmailsPlaceholder')}
              data-testid="ob-notification-emails-input"
            />
            <p className="text-[11px] text-[#94A3B8] leading-snug">{t('onlineBooking.notificationEmailsHelp')}</p>
          </div>

          {/* Sesli Bildirim */}
          <div className="rounded-lg border border-[#E5E5E5] bg-[#F9FAFB] p-3 mt-2" data-testid="ob-sound-row">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#0F172A]">{t('onlineBooking.soundTitle')}</p>
                <p className={`text-xs mt-0.5 ${soundOn ? 'text-emerald-700' : 'text-[#595959]'}`}>
                  {soundOn ? t('onlineBooking.soundOn') : t('onlineBooking.soundOff')}
                </p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {soundOn ? (
                  <>
                    <Button type="button" variant="outline" size="sm" onClick={handleTestSound} data-testid="ob-sound-test-btn">
                      {t('onlineBooking.soundPlayTest')}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={handleDisableSound} data-testid="ob-sound-off-btn">
                      <VolumeX className="w-4 h-4 mr-1" /> {t('onlineBooking.soundDisable')}
                    </Button>
                  </>
                ) : (
                  <Button type="button" size="sm" onClick={handleEnableSound} className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="ob-sound-on-btn">
                    <Volume2 className="w-4 h-4 mr-1" /> {t('onlineBooking.soundEnable')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tarayıcı Push */}
          <div className="rounded-lg border border-[#E5E5E5] bg-[#F9FAFB] p-3 mt-2" data-testid="ob-push-row">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#0F172A]">{t('onlineBooking.pushTitle')}</p>
                {!pushState.supported ? (
                  <p className="text-xs text-[#9CA3AF] mt-0.5">{t('onlineBooking.pushUnsupported')}</p>
                ) : pushState.permission === 'denied' ? (
                  <p className="text-xs text-amber-700 mt-0.5">{t('onlineBooking.pushDenied')}</p>
                ) : pushState.subscribed ? (
                  <p className="text-xs text-emerald-700 mt-0.5">{t('onlineBooking.pushSubscribed')}</p>
                ) : (
                  <p className="text-xs text-[#595959] mt-0.5">{t('onlineBooking.pushAvailable')}</p>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {pushState.subscribed ? (
                  <>
                    <Button type="button" variant="outline" size="sm" onClick={handleTestPush} disabled={pushBusy} data-testid="ob-push-test-btn">
                      {t('onlineBooking.pushTest')}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={handleDisablePush} disabled={pushBusy} data-testid="ob-push-off-btn">
                      <BellOff className="w-4 h-4 mr-1" /> {t('onlineBooking.pushDisable')}
                    </Button>
                  </>
                ) : (
                  <Button type="button" size="sm" onClick={handleEnablePush} disabled={pushBusy || !pushState.supported} className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="ob-push-on-btn">
                    <Bell className="w-4 h-4 mr-1" /> {t('onlineBooking.pushEnable')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-[#595959] italic">{t('onlineBooking.workingHoursNote')}</p>
      </CardContent>
    </Card>
  );
};

export default OnlineBookingCard;
